/**
 * DurationInput Component
 * 
 * User-friendly duration input that allows entry in hours and minutes format.
 * Converts to total minutes for storage and calculation.
 */

import { useState, useEffect } from 'react';
import { cn } from '../../lib/utils';
import { Clock } from 'lucide-react';

interface DurationInputProps {
  value?: number; // Total minutes
  onChange: (minutes: number) => void;
  className?: string;
  disabled?: boolean;
  error?: string;
  label?: string;
  placeholder?: string;
  minMinutes?: number;
  maxMinutes?: number;
}

export function DurationInput({
  value = 0,
  onChange,
  className,
  disabled = false,
  error,
  label,
  placeholder = "e.g. 1h 30m",
  minMinutes = 15,
  maxMinutes = 24 * 60 // 24 hours
}: DurationInputProps) {
  const [inputValue, setInputValue] = useState('');
  const [isValid, setIsValid] = useState(true);

  // Convert minutes to display format
  const formatMinutesToDisplay = (totalMinutes: number): string => {
    if (totalMinutes === 0) return '';
    
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    
    if (hours > 0 && minutes > 0) {
      return `${hours}h ${minutes}m`;
    } else if (hours > 0) {
      return `${hours}h`;
    } else {
      return `${minutes}m`;
    }
  };

  // Parse input text to minutes
  const parseInputToMinutes = (input: string): { minutes: number; valid: boolean } => {
    if (!input.trim()) {
      return { minutes: 0, valid: true };
    }

    // Clean up input - remove extra spaces, normalize
    const cleanInput = input.toLowerCase().trim().replace(/\s+/g, ' ');
    
    // Patterns to match:
    // "1h 30m", "1h30m", "1 hour 30 minutes", "90m", "90 minutes", "1.5h", "1.5 hours"
    const patterns = [
      /^(\d+(?:\.\d+)?)\s*h(?:our)?s?\s*(\d+)\s*m(?:in)?(?:ute)?s?$/,  // "1h 30m", "1hour 30min"
      /^(\d+(?:\.\d+)?)\s*h(?:our)?s?$/,                                // "1h", "1.5hours"
      /^(\d+)\s*m(?:in)?(?:ute)?s?$/,                                   // "90m", "90minutes"
      /^(\d+(?:\.\d+)?)\s*$/,                                           // "90" (assume minutes)
    ];

    // Try pattern 1: "1h 30m"
    let match = cleanInput.match(patterns[0]);
    if (match) {
      const hours = parseFloat(match[1]);
      const minutes = parseInt(match[2]);
      const totalMinutes = hours * 60 + minutes;
      const valid = totalMinutes >= minMinutes && totalMinutes <= maxMinutes;
      return { minutes: totalMinutes, valid };
    }

    // Try pattern 2: "1h" or "1.5h"
    match = cleanInput.match(patterns[1]);
    if (match) {
      const hours = parseFloat(match[1]);
      const totalMinutes = hours * 60;
      const valid = totalMinutes >= minMinutes && totalMinutes <= maxMinutes;
      return { minutes: totalMinutes, valid };
    }

    // Try pattern 3: "90m"
    match = cleanInput.match(patterns[2]);
    if (match) {
      const totalMinutes = parseInt(match[1]);
      const valid = totalMinutes >= minMinutes && totalMinutes <= maxMinutes;
      return { minutes: totalMinutes, valid };
    }

    // Try pattern 4: "90" (assume minutes)
    match = cleanInput.match(patterns[3]);
    if (match) {
      const totalMinutes = parseFloat(match[1]);
      const valid = totalMinutes >= minMinutes && totalMinutes <= maxMinutes;
      return { minutes: totalMinutes, valid };
    }

    return { minutes: 0, valid: false };
  };

  // Update input when value prop changes
  useEffect(() => {
    const displayValue = formatMinutesToDisplay(value);
    setInputValue(displayValue);
    setIsValid(true);
  }, [value]);

  // Handle input change
  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newInput = event.target.value;
    setInputValue(newInput);

    const { minutes, valid } = parseInputToMinutes(newInput);
    setIsValid(valid);

    if (valid) {
      onChange(minutes);
    }
  };

  // Handle blur - clean up formatting
  const handleBlur = () => {
    const { minutes, valid } = parseInputToMinutes(inputValue);
    if (valid) {
      const cleanDisplay = formatMinutesToDisplay(minutes);
      setInputValue(cleanDisplay);
      onChange(minutes);
    }
  };

  const baseInputClasses = `
    w-full h-12 px-4 rounded-lg border transition-all duration-200
    bg-oro-surface border-oro-border text-text-primary
    focus:border-accent focus:ring-2 focus:ring-accent/20 focus:outline-none
    disabled:opacity-50 disabled:cursor-not-allowed
    text-body
  `;

  const errorClasses = (error || !isValid) ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20' : '';

  const displayError = error || (!isValid && inputValue ? 'Invalid duration format' : '');

  return (
    <div className={cn('space-y-2', className)}>
      {label && (
        <label className="block text-sm font-medium text-text-primary">
          {label}
        </label>
      )}
      
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <Clock className="h-4 w-4 text-text-muted" />
        </div>
        
        <input
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onBlur={handleBlur}
          placeholder={placeholder}
          disabled={disabled}
          className={cn(
            baseInputClasses,
            errorClasses,
            'pl-10' // Space for clock icon
          )}
        />
      </div>

      {/* Help text and error */}
      <div className="space-y-1">
        {!displayError && (
          <p className="text-xs text-text-muted">
            Examples: 1h 30m, 2h, 90m, 1.5h
          </p>
        )}
        
        {displayError && (
          <p className="text-sm text-red-500 flex items-center gap-1">
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            {displayError}
          </p>
        )}
      </div>
    </div>
  );
}

/**
 * Quick duration presets for common event lengths
 */
interface DurationPresetsProps {
  onSelect: (minutes: number) => void;
  className?: string;
}

export function DurationPresets({ onSelect, className }: DurationPresetsProps) {
  const presets = [
    { label: '30 min', minutes: 30 },
    { label: '1 hour', minutes: 60 },
    { label: '1h 30m', minutes: 90 },
    { label: '2 hours', minutes: 120 },
    { label: '2h 30m', minutes: 150 },
    { label: '3 hours', minutes: 180 },
  ];

  return (
    <div className={cn('grid grid-cols-3 gap-2', className)}>
      {presets.map((preset) => (
        <button
          key={preset.minutes}
          onClick={() => onSelect(preset.minutes)}
          className="px-3 py-2 text-sm rounded-lg bg-oro-surface-alt border border-oro-border text-text-secondary hover:text-text-primary hover:border-accent transition-colors text-center"
        >
          {preset.label}
        </button>
      ))}
    </div>
  );
}